import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import PublicProgramSection from '../components/program/PublicProgramSection';
import { CoverPages } from '../components/program/ProgramPages';
import { PROGRAM_ANCHOR } from '../components/EventImageLink';
import { LIVE_HASH } from '../components/program/liveProgram';

// Matches HomeWrapper on the main page: full-bleed color sections (like
// SectionWrapper and ClosingSection below) get inset from the viewport
// edges instead of running edge-to-edge, so this page reads the same way
// the home page does rather than looking like a different site.
const PageWrapper = styled.div`
  padding-left: 2rem;
  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Crumb = styled.nav`
  max-width: 1700px;
  margin: 0 auto;
  padding: 1.6rem 2rem 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span.sep { margin: 0 0.4rem; }
  span.current { color: #fff; font-weight: bold; }
`;

const SectionWrapper = styled.section`
  background-color: #90C4D9;
  padding: 70px 20px 40px;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    padding: 40px 0px 0px;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 3rem;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 70px;
  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const EventWrapper = styled.div`
  background: transparent;
  padding: 20px;
  margin-bottom: 10px;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1700px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  padding-right: 20px;

  @media (max-width: 1024px) {
    padding-right: 0;
    text-align: center;
    max-width: 600px;
    width: 100%;
  }
`;

const MiddleColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    max-width: 600px;
    width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 20px;

  @media (max-width: 1024px) {
    padding-left: 0;
    max-width: 600px;
    width: 100%;
  }
`;

const EventTitle = styled.h1`
  font-size: 3rem;
  margin-top: 40px;
  margin-bottom: 0px;
  display: inline-block;
  padding-bottom: 6px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);
  background-size: 100% 4px;
  background-position: 0 bottom;
  background-repeat: no-repeat;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-image: linear-gradient(to right, transparent 50%, #90C4D9 50%);
    background-size: 8px 4px;
    background-repeat: repeat-x;
  }

  @media (max-width: 1024px) {
    font-size: 1.6rem;
    margin-top: 10px;
    text-align: center;
  }
`;

const EventMeta = styled.p`
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 0.6rem;
  margin-bottom: 0;

  @media (max-width: 1024px) {
    font-size: 0.95rem;
  }
`;

const SecondaryHeadline = styled.h4`
  font-size: 1.8rem;
  font-weight: bold;
  margin-top: 40px;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1.4rem;
    margin-top: 20px;
    margin-bottom: 10px;
  }
`;

const BulletList = styled.ul`
  list-style-type: disc;
  padding-left: 20px;
  margin-bottom: 20px;
  font-size: 1.4rem;

  @media (max-width: 1024px) {
    display: inline-block;
    text-align: left;
    font-size: 1rem;
    margin-bottom: 10px;
  }
`;

const TextContent = styled.p`
  font-size: 1.4rem;
  line-height: 1.5;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const EventImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;

const EventButton = styled.button`
  background-color: #47ABDB;
  text-decoration: none;
  height: 4.0rem;
  font-weight: bold;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 1.8rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 300px;
  text-align: center;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: #0056b3;
  }
`;

// "SUBMIT ABSTRACT" is now the only label this button ever shows (English-only,
// no more German "BEITRAG EINREICHEN" overflow to work around) - same size as
// EventButton, just forced to one line.
const SubmitAbstractButton = styled(EventButton)`
  white-space: nowrap;
  padding-left: 0;
  padding-right: 0;
`;

const EventInfo = styled.div`
  font-size: 1.4rem;
  margin-bottom: 40px;
  text-align: left;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const EventButtonTitle = styled.p`
  margin-bottom: 0.1rem;
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const EventDetails = styled.p`
  margin: 0;
  width: 100%;
  margin-top: 0.2rem;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const SubText = styled.p`
  font-weight: bold;
  font-size: 1.4rem;
  line-height: 1.5;
  margin-top: 40px;
  padding: 0 40px 0 0;
  @media (max-width: 1024px) {
    font-size: 1rem;
    padding: 0;
    text-align: center;
  }
`;

const PastNote = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 300px;
  margin-bottom: 1.5rem;
`;

// Optional free-text sections above / below the program - the "Text Above/Below
// Speakers" fields of the event admin form, whose labels still name the old
// speaker grid these used to surround. The accent bar echoes the gradient
// underline on EventTitle so these read as part of the same page rather than a
// bolted-on paragraph.
const BufferSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 56px 40px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 36px 24px;
  }
`;

// The event's own program text - the "Program Text (DE/EN)" field of the event admin form, which the
// old page showed under the PROGRAM button in the right column. It belongs to the event data, not to
// the public program, so it shows whenever the event has one; the program section below it shows only
// what the program itself publishes (plan §7.6).
const ProgramText = styled.p`
  max-width: 42rem;
  margin: 1.5rem auto 0;
  padding: 0 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.4rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    padding: 0 1.5rem;
  }
`;

const BufferAccent = styled.div`
  width: 70px;
  height: 4px;
  margin: 0 auto 28px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }
`;

const BufferText = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1.7;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

// The event's long-form content (textHeadline block and subText) sits below
// the hero container so the hero stays a compact promo card like the
// homepage; the body text gets room on the page background.
const EventContentSection = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 40px 10px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 36px 24px 0;
  }
`;

// The below-speakers text sits directly above the footer, which shares the
// same page-wide background gradient - without a container it visually
// bled straight into it. Rather than inventing a new container style, this
// reuses the site's actual convention for separating sections: a full-bleed
// flat-color band, the same #90C4D9 as the hero SectionWrapper above and the
// same bold centered-white treatment as SectionSubTitle.
const ClosingSection = styled.section`
  background-color: #90C4D9;
  padding: 70px 20px;
  margin-top: 60px;
  margin-bottom: 90px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-top: 30px;
    margin-bottom: 50px;
  }
`;

const ClosingInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ClosingText = styled.p`
  color: white;
  font-weight: bold;
  font-size: 1.7rem;
  line-height: 1.5;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const NotFoundWrapper = styled.div`
  max-width: 700px;
  margin: 6rem auto;
  padding: 0 2rem;
  text-align: center;
  color: white;

  a {
    color: #47ABDB;
  }
`;

const EventDetail = ({ openRegistrationModal }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { language } = useLanguage();
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The program section of this page owns its own loading (decision 1 of plan
  // §7.6: the public program is part of the event page, not a button in the
  // right column); the page only needs to know whether there is one, because a
  // page without it must not show the text that introduces it.
  const [programStatus, setProgramStatus] = useState('loading');
  // The public program, once loaded: its title pages stand where the event's main image does.
  const [program, setProgram] = useState(null);

  const getContent = (defaultContent, enContent) => {
    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setEvent(null);

    axios.get(`${process.env.REACT_APP_API_URL}/api/cms/event/public/get-by-slug/${slug}`)
      .then((response) => {
        if (cancelled) return;
        setEvent(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!event) return undefined;
    const previousTitle = document.title;
    const headline = getContent(event.mainHeadline, event.mainHeadlineEN) || getContent(event.bigHeadline, event.bigHeadlineEN);
    document.title = headline ? `${headline} - HEARTS Conference` : 'HEARTS Conference';
    return () => { document.title = previousTitle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, language]);

  // A link to #programm (the PROGRAM button of an event card on the home page) lands on the program
  // section, once the program it waits for is in and the images above it (the title pages) have
  // their height - a scroll started earlier ends in the wrong place when they push the page down. The
  // other pages' "Live" button (LiveProgramButton, LIVE_HASH) lands there too, the program open at
  // today; that anchor is then dropped, so the back button later puts back where it was left.
  useEffect(() => {
    if (programStatus !== 'ready' || (hash !== `#${PROGRAM_ANCHOR}` && hash !== LIVE_HASH)) return undefined;
    const el = document.getElementById(PROGRAM_ANCHOR);
    if (!el || typeof el.scrollIntoView !== 'function') return undefined;
    let cancelled = false;
    const above = [...document.images].filter((img) => !img.complete
      && (img.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
    const loaded = above.map((img) => new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    }));
    Promise.race([Promise.all(loaded), new Promise((resolve) => setTimeout(resolve, 3000))]).then(() => {
      if (cancelled) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (hash === LIVE_HASH) {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      }
    });
    return () => { cancelled = true; };
  }, [programStatus, hash]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleRegisterClick = () => {
    if (typeof openRegistrationModal === 'function') {
      openRegistrationModal(event);
    }
  };

  const handleSubmitAbstractClick = () => {
    navigate(`/call-for-abstracts?eventId=${event.id}`);
  };

  if (loading) return null;

  if (notFound || !event) {
    return (
      <NotFoundWrapper>
        <h2>{t('event.notFound.title', 'Event not found')}</h2>
        <p>{t('event.notFound.body', "This event doesn't exist, or is no longer available.")}</p>
        <Link to="/">{t('event.notFound.link', 'Back to the homepage')}</Link>
      </NotFoundWrapper>
    );
  }

  // No hardcoded placeholder here on purpose - an event with no headline
  // entered in the CMS yet should show no title, not invented copy.
  const displayName = getContent(event.mainHeadline, event.mainHeadlineEN)
    || getContent(event.bigHeadline, event.bigHeadlineEN)
    || '';
  const isPast = Boolean(event.dateTime) && new Date(event.dateTime) < new Date();
  const metaLine = [formatDate(event.dateTime), event.location].filter(Boolean).join(' - ');

  return (
    <PageWrapper>
      <Crumb aria-label="Breadcrumb">
        <Link to="/">{t('nav.home', 'Home')}</Link>
        {displayName && (
          <>
            <span className="sep">/</span>
            <span className="current">{displayName}</span>
          </>
        )}
      </Crumb>

      <SectionWrapper>
        {(event.bigHeadline || event.bigHeadlineEN) && (
          <SectionTitle>{getContent(event.bigHeadline, event.bigHeadlineEN)}</SectionTitle>
        )}
        {(event.subHeadline || event.subHeadlineEN) && (
          <SectionSubTitle>{getContent(event.subHeadline, event.subHeadlineEN)}</SectionSubTitle>
        )}

        <EventWrapper>
          <ContentWrapper>
            <LeftColumn>
              {displayName && <EventTitle>{displayName}</EventTitle>}
              {metaLine && <EventMeta>{metaLine}</EventMeta>}

              {event.bulletPointsHeadline && (
                <>
                  <SecondaryHeadline>
                    {getContent(event.bulletPointsHeadline.headline, event.bulletPointsHeadline.headlineEN)}
                  </SecondaryHeadline>
                  <BulletList>
                    {(getContent(event.bulletPointsHeadline.bulletPoints, event.bulletPointsHeadline.bulletPointsEN) || [])
                      .map((point, index) => <li key={index}>{point}</li>)}
                  </BulletList>
                </>
              )}
            </LeftColumn>

            <MiddleColumn>
              {/* the main image only once it is known that no title pages replace it */}
              {program?.pages?.cover?.length > 0 && <CoverPages program={program} alt={displayName} />}
              {programStatus !== 'loading' && !(program?.pages?.cover?.length > 0) && event.mainMediaUrl && (
                <EventImage
                  src={`${process.env.REACT_APP_API_URL || ''}${event.mainMediaUrl}`}
                  alt={displayName}
                />
              )}
            </MiddleColumn>

            {(!isPast || event.acceptsSubmissions) && (
              <RightColumn>
                {!isPast && (
                  <>
                    <EventButton onClick={handleRegisterClick}>
                      <FormattedMessage
                        id={event.requiresApproval ? 'event.register.pre' : 'event.register'}
                        defaultMessage={event.requiresApproval ? 'PRE-REGISTER' : 'REGISTER NOW'}
                      />
                    </EventButton>
                    <EventInfo>
                      {displayName && <EventButtonTitle>{displayName}</EventButtonTitle>}
                      {metaLine && <EventDetails>{metaLine}</EventDetails>}
                    </EventInfo>
                  </>
                )}
                {isPast && (
                  <PastNote>
                    {t('event.past.note', 'This event took place on {date}.', { date: formatDate(event.dateTime) })}
                  </PastNote>
                )}
                {event.acceptsSubmissions && (
                  <SubmitAbstractButton onClick={handleSubmitAbstractClick}>
                    <FormattedMessage id="event.submitAbstract" defaultMessage="SUBMIT ABSTRACT" />
                  </SubmitAbstractButton>
                )}
              </RightColumn>
            )}
          </ContentWrapper>
        </EventWrapper>
      </SectionWrapper>

      {/* The text that introduces the program only has something to introduce
          when the event actually has a public program. */}
      {programStatus === 'ready' && (event.textAboveSpeakers || event.textAboveSpeakersEN) && (
        <BufferSection>
          <BufferAccent />
          <BufferText>{getContent(event.textAboveSpeakers, event.textAboveSpeakersEN)}</BufferText>
        </BufferSection>
      )}

      {/* The event's own program text, above the program itself - the old page showed it in the right
          column under the PROGRAM button (see ProgramText's own comment). */}
      {(event.programText || event.programTextEN) && (
        <ProgramText>{getContent(event.programText, event.programTextEN)}</ProgramText>
      )}

      <div id={PROGRAM_ANCHOR}>
        <PublicProgramSection slug={slug} onStatus={setProgramStatus} onProgram={setProgram} />
      </div>

      {(event.textHeadline || (event.subText || event.subTextEN)) && (
        <EventContentSection>
          {event.textHeadline && (
            <>
              <SecondaryHeadline>
                {getContent(event.textHeadline.headline, event.textHeadline.headlineEN)}
              </SecondaryHeadline>
              <TextContent>
                {getContent(event.textHeadline.textContent, event.textHeadline.textContentEN)}
              </TextContent>
            </>
          )}
          {(event.subText || event.subTextEN) && (
            <SubText>{getContent(event.subText, event.subTextEN)}</SubText>
          )}
        </EventContentSection>
      )}

      {(event.textBelowSpeakers || event.textBelowSpeakersEN) && (
        <ClosingSection>
          <ClosingInner>
            <BufferAccent />
            <ClosingText>{getContent(event.textBelowSpeakers, event.textBelowSpeakersEN)}</ClosingText>
          </ClosingInner>
        </ClosingSection>
      )}
    </PageWrapper>
  );
};

export default EventDetail;
